import { useCallback, useMemo, useState } from "react";

import clsx from "clsx";
import moment from "moment";
import { useToasts } from "react-toast-notifications";

import { useUser } from "@components/user-context";
import SquadronArriving from "@features/mmm/assets/squadron-arriving.svg";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type NotifyMMMDataIsReady,
  type NotifyMMMDataIsReadyVariables,
  type UpsertMMMCycleDataInput,
  type UpsertMMMCycleDataInputVariables,
} from "@north-beam/nb-client-side";
import { PrimaryButton } from "@shared/buttons";
import { Checkbox } from "@shared/checkbox";

import { useMMMClient } from "../hooks/use-mmm-client";
import { useSelectedCycle } from "../hooks/use-selected-cycle";

import { NOTIFY_MMM_DATA_READY } from "./notify-cs";
import { SpendByChannels } from "./spend-by-channels";
import { UPSERT_DATA_INPUT } from "./upsert-mutation";

export const PopulatingData = () => {
  const [mmmClient] = useMMMClient();
  const { selectedCycle } = useSelectedCycle();
  const { user } = useUser();

  const [hasUpdatedADI, _setHasUpdatedADI] = useState(
    selectedCycle.cycleInputStage?.has_updated_adi ?? false,
  );
  const [hasUpdatedCOE, _setHasUpdatedCOE] = useState(
    selectedCycle.cycleInputStage?.has_updated_coe ?? false,
  );
  const [hasUpdatedSpend, _setHasUpdatedSpend] = useState(
    selectedCycle.cycleInputStage?.has_updated_spend ?? false,
  );

  const [updateDataInput] = useNorthbeamMutation<
    UpsertMMMCycleDataInput,
    UpsertMMMCycleDataInputVariables
  >(UPSERT_DATA_INPUT);

  const { addToast } = useToasts();
  const [notifyCS, { loading: notifyingCSLoading, data: notifyCSData }] =
    useNorthbeamMutation<NotifyMMMDataIsReady, NotifyMMMDataIsReadyVariables>(
      NOTIFY_MMM_DATA_READY,
      {
        onCompleted: () => {
          addToast(
            "Our CS team has been notified and will get back to you shortly!",
            { appearance: "success" },
          );
        },
      },
    );

  const gqlVariables = useMemo(
    () => ({
      id: selectedCycle.cycleInputStage?.id,
      has_updated_adi: hasUpdatedADI,
      has_updated_coe: hasUpdatedCOE,
      has_updated_spend: hasUpdatedSpend,
      cycle_id: selectedCycle.id,
    }),
    [
      hasUpdatedADI,
      hasUpdatedCOE,
      hasUpdatedSpend,
      selectedCycle.cycleInputStage?.id,
      selectedCycle.id,
    ],
  );

  const setHasUpdatedADI = useCallback(
    (i: boolean) => {
      _setHasUpdatedADI(i);
      updateDataInput({
        variables: {
          ...gqlVariables,
          has_updated_adi: i,
        },
      });
    },
    [gqlVariables, updateDataInput],
  );
  const setHasUpdatedCOE = useCallback(
    (i: boolean) => {
      _setHasUpdatedCOE(i);
      updateDataInput({
        variables: {
          ...gqlVariables,
          has_updated_coe: i,
        },
      });
    },
    [gqlVariables, updateDataInput],
  );
  const setHasUpdatedSpend = useCallback(
    (i: boolean) => {
      _setHasUpdatedSpend(i);
      updateDataInput({
        variables: {
          ...gqlVariables,
          has_updated_spend: i,
        },
      });
    },
    [gqlVariables, updateDataInput],
  );

  const upTo4weeks = moment(selectedCycle.date_range.start_date)
    .subtract(4, "weeks")
    .format("MMM DD, YYYY");
  return (
    <div className="flex-center items-start gap-4 h-[790px]">
      <div className="bg-v2-primary border border-v2-secondary rounded-lg flex items-stretch p-10 w-[40rem]">
        <div className="flex-1 flex flex-col items-center gap-6">
          <img
            src={SquadronArriving}
            alt="Squadron Arriving"
            className="h-[10rem]"
          />
          <div className=" text-interstellar font-semibold text-2xl text-center w-[22rem]">
            Make sure your data is up to date for the prior period
          </div>
          <div className="text-center w-[22rem]">
            Up-to-date data is required to start creating new MMM+ settings.
          </div>
          <div className="flex flex-col items-center gap-4">
            {mmmClient?.input_google_sheet_url && (
              <div>
                <a
                  className={clsx(
                    "block button primary-button sm text-sm no-underline font-bold px-6 py-2 rounded-full",
                    "leading-5 border-[1px] border-cosmic",
                  )}
                  href={mmmClient.input_google_sheet_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa-regular fa-file-spreadsheet mr-2"></i>
                  UPDATE MY MMM+ DATA
                </a>
              </div>
            )}
          </div>
          <div className="flex-1 flex items-center justify-center mt-3">
            <div className="flex flex-col">
              <h5 className="font-bold text-xs self-center ">
                PLEASE CONFIRM THE FOLLOWING ARE UP TO DATE:
              </h5>
              <label className="cursor-pointer m-0">
                <div className="flex items-center gap-3 text-sm border rounded-lg  mt-1 py-2 px-2 justify-between">
                  <div className="flex gap-2 items-center ">
                    <Checkbox
                      value={hasUpdatedADI}
                      onChange={() => setHasUpdatedADI(!hasUpdatedADI)}
                    />
                    MMM+ Offline data (e.g Wholesale revenue, email, sms )
                  </div>
                  <Pill text={`Up to ${upTo4weeks}`} />
                </div>
              </label>
              <label className="cursor-pointer  m-0">
                <div className="flex items-center gap-3 text-sm border rounded-lg mt-1 py-2 px-2 justify-between">
                  <div className="flex gap-2 items-center ">
                    <Checkbox
                      value={hasUpdatedCOE}
                      onChange={() => setHasUpdatedCOE(!hasUpdatedCOE)}
                    />
                    MMM+ Calendar of events
                  </div>
                  <Pill text={`Up to ${upTo4weeks}`} />
                </div>
              </label>
              <label className="cursor-pointer  m-0">
                <div className="flex items-center text-sm border rounded-lg mt-1 py-2 px-2 justify-between">
                  <div className="flex gap-2 items-center ">
                    <Checkbox
                      value={hasUpdatedSpend}
                      onChange={() => setHasUpdatedSpend(!hasUpdatedSpend)}
                    />
                    MTA influencer and spend spreadsheets
                  </div>
                  <Pill text="Until end of month" />
                </div>
              </label>
            </div>
          </div>
          <PrimaryButton
            isDisabled={
              !!notifyCSData ||
              !hasUpdatedADI ||
              !hasUpdatedCOE ||
              !hasUpdatedSpend
            }
            loading={notifyingCSLoading}
            className="w-[17rem]"
            onClick={() => {
              notifyCS({
                variables: {
                  cycleId: selectedCycle.id,
                },
              });
            }}
          >
            NOTIFY CS TEAM THAT MY DATA IS READY
          </PrimaryButton>
          <div className="text-xs ">
            <span className="font-semibold">Have questions or need help? </span>
            <a
              className={clsx("font-bold", "leading-5  border-cosmic")}
              href={`mailto:mmm-success@northbeam.io?subject=MMM - ${user.companyName} is having trouble`}
              rel="noopener noreferrer"
              target="_blank"
            >
              Contact us for support
            </a>
          </div>
        </div>
      </div>
      <SpendByChannels />
    </div>
  );
};

function Pill({ text }: { text: string }) {
  return (
    <div
      className={clsx(
        "flex items-center p-4  bg-cosmic-4 text-primary font-semibold h-4  ",
        "rounded-full cursor-pointer",
      )}
    >
      <span className="text-xs">{text}</span>
    </div>
  );
}
